<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Swiper, { Pagination, Navigation, Autoplay } from 'swiper';

const props = defineProps({
    heading: {
        type: String,
        required: true
    },
    size: {
        type: String,
        required: true
    },
    editMode: {
        type: Boolean,
        required: true,
        default: false
    }
});

Swiper.use([Pagination, Navigation, Autoplay]);

const store = useStore();
const swiperInstance = ref(null);
const swiperRef = ref(`swiper-${Math.random().toString(36).substr(2, 8)}`);

const banners = computed(() => store.getters['dashboard_banners/all']);

const isWebm = (banner) => banner?.banner_image?.endsWith('.webm') && banner?.banner_image_alt?.endsWith('.webm');

const isImage = (banner) => {
    const exts = ['.jpg', '.jpeg', '.png'];

    return exts.some((ext) => banner?.banner_image?.endsWith(ext)) && exts.some((ext) => banner?.banner_image_alt?.endsWith(ext));
};

const validBanners = computed(() => (Array.isArray(banners.value) ? banners.value : []).filter((banner) => isWebm(banner) || isImage(banner)));
const getBannerSource = (banner) => (props.size === 'small' ? banner.banner_image : banner.banner_image_alt);

const sanitizeLink = (link) => {
    link = link.trim().replace(/[\s<>`"']/g, '');

    if (!link.startsWith('http://') && !link.startsWith('https://')) {
        throw new Error('Unsupported protocol. Only HTTP and HTTPS are allowed.');
    }

    return link;
};

/**
 * @param link string
 * @returns boolean
 */
const validateLink = (link) => {
    return link && (link.startsWith('http://') || link.startsWith('https://'));
};

/**
 * @param link string
 * @returns void
 */
const openLink = (link) => {
    window.open(link, '_blank');
};

/**
 * @param link string
 * @returns void
 * @throws Error if link is invalid
 */
const navigateToLink = (link) => {
    try {
        const sanitizedLink = sanitizeLink(link);

        if (!validateLink(sanitizedLink)) throw new Error('Invalid link');

        openLink(sanitizedLink);
    } catch (e) {
        console.error(e.message);
    }
};

/**
 * Initialize the Swiper slider with specified settings.
 * @returns void
 */
const initSlider = () => {
    if (swiperInstance.value) {
        swiperInstance.value.destroy(true, true);
    }

    swiperInstance.value = new Swiper(`.${swiperRef.value}`, {
        pagination: {
            el: `.${swiperRef.value} .swiper-pagination`,
            clickable: true
        },
        autoplay: {
            delay: 5000,
            disableOnInteraction: false
        },
        speed: 3000,
        slidesPerView: 1,
        spaceBetween: 10,
        centeredSlides: true,
        loop: true
    });
};

const refreshSwiper = () => {
    nextTick(() => {
        initSlider();
    });
};

onMounted(() => {
    refreshSwiper();
});

watch([() => props.size, validBanners], () => {
    refreshSwiper();
});
</script>

<template>
    <div :class="['swiper', swiperRef, 'rounded-xl', { 'bg-white': editMode }]">
        <div class="swiper-wrapper" :class="{ invisible: editMode || validBanners.length === 0 }">
            <div v-for="(banner, index) in validBanners" :key="index" class="swiper-slide">
                <div class="rounded-xl cursor-pointer" @click="navigateToLink(banner.link)">
                    <video v-if="isWebm(banner)" autoplay loop muted class="block w-full h-full">
                        <source :src="getBannerSource(banner)" type="video/webm" />
                    </video>
                    <img v-else class="rounded-xl" :src="getBannerSource(banner)" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.swiper .swiper-slide {
    opacity: 0.6;
    z-index: 1;
    transition: opacity 1s ease;
}

.swiper .swiper-slide.swiper-slide-active {
    opacity: 1;
    z-index: 2;
}
</style>
